/**
 * Provides functionality to construct component instances from DOM elements and manage component initialization.
 * @module component_constructor
 */

import {RankersNamespaceKey, ensureRankersNamespace} from "../constants/rankers_namespace";
import {getThrottledFunction} from "../helpers/throttle";
import {ComponentRegistry} from "./component_registry";

/**
 * How long (ms) to wait before sweeping for disconnected components, so that a burst of DOM
 * mutations reaps once. Matches the ComponentInitializer's throttle on the construction side.
 * The delay is also what makes the sweep immune to move-vs-remove: `isConnected` is read AFTER
 * the window, so an element detached and re-inserted within it is simply still connected.
 */
const ReaperThrottleDelay = 50;

/**
 * Name of the property used to mark elements as "componentized".
 * When an element is initialized with a Component then we add the componentTypeKey to the element's "componentizedWith" array.
 * The 'ComponentConstructor.isElementComponentized` method can check this property to see if an element is already inited with any specific type of component.
 */
export const ComponentizedPropertyName = 'componentizedWith';


/**
 * Class responsible for constructing and initializing components.
 * ComponentConstructor can find elements that are not yet initialized with a component.
 * This class can search multiple root elements and return an array of instances of the specified component class.
 * The class will  mark the elements as initialized so that they are not initialized again.
 */
export class ComponentConstructor {

    /**
     * Checks if an element is already initialized with a specific component type.
     * @param {Element} element - The DOM element to check.
     * @param {string} componentTypeKey - The key representing the component type.
     * @returns {boolean} True if the element is initialized, false otherwise.
     */
    static isElementComponentized(element, componentTypeKey) {
        if (!element.hasOwnProperty(ComponentizedPropertyName)) return false;
        if (!Array.isArray(element[ComponentizedPropertyName])) return false;
        return element[ComponentizedPropertyName].includes(componentTypeKey);
    }

    /**
     * Marks an element as initialized with a specific component type.
     * @param {Element} element - The DOM element to mark.
     * @param {string} componentTypeKey - The key representing the component type.
     */
    static markElementAsComponentized(element, componentTypeKey) {
        if (!element) return;
        if (!componentTypeKey) return;
        if (element.hasOwnProperty(ComponentizedPropertyName)) {
            if (!element[ComponentizedPropertyName].includes(componentTypeKey)) element[ComponentizedPropertyName].push(componentTypeKey);
        } else {
            element[ComponentizedPropertyName] = [componentTypeKey];
        }
    }

    /**
     * Unmarks an element, so it is no longer considered initialized with the component type.
     * Called by `Component#destroy` — a destroyed element which remains in (or returns to) the
     * DOM is then eligible for re-componentization by the initializer sweeps, rather than being
     * permanently dead.
     * @param {Element} element - The DOM element to unmark.
     * @param {string} componentTypeKey - The key representing the component type.
     */
    static unmarkElementAsComponentized(element, componentTypeKey) {
        if (!element) return;
        if (!componentTypeKey) return;
        if (!Array.isArray(element[ComponentizedPropertyName])) return;
        const index = element[ComponentizedPropertyName].indexOf(componentTypeKey);
        if (index !== -1) element[ComponentizedPropertyName].splice(index, 1);
    }

    constructor() {
        this.ID = Math.random().toString(36).substr(2, 3);
        this.rootNodes = [document.documentElement];
        this.componentInitializers = [];
        this.observeDisconnectedComponents();
    }

    /**
     * Gets the current root nodes for component construction.
     * @returns {Array} Array of root nodes.
     */
    getRootNodes() {
        return this.rootNodes;
    }

    /**
     * Adds a new root node for component construction.
     * Then we immediately observe the new root node for any elements that need to be "componentized".
     * @param {Element} node - The new root node to add.
     */
    addRootNode(node) {
        this.rootNodes.push(node);
        this.observeElementsForComponents();
        this.observeDisconnectedComponents();
    }

    /**
     * Removes a root node from component construction.
     * @param {Element} node - The root node to remove.
     */
    removeRootNode(node) {
        this.rootNodes = this.rootNodes.filter(rootNode => rootNode !== node);
        this.observeDisconnectedComponents();
    }

    /**
     * The reaper: the other half of the component lifecycle.
     *
     * ComponentInitializer watches for ARRIVING elements and componentizes them. Nothing watched
     * for DEPARTING ones — so a component whose element was discarded (every pane re-injection,
     * every AJAX-replaced region) lived on for the life of the page. This one MutationObserver
     * closes that: on any DOM change under the root nodes, it sweeps the registry and destroys
     * every component whose element is no longer connected.
     *
     * It lives here, on the ComponentConstructor singleton, because this class already owns the
     * root-node list and is already shared across bundles: one observer, regardless of how many
     * component types exist. Sweeping the registry — rather than walking each mutation's
     * removedNodes subtrees — is both less code and immune to a node being MOVED rather than
     * removed (see ReaperThrottleDelay).
     *
     * Note it can only see registry-held (loader-constructed) components. Parent-constructed
     * children are destroyed by their parent's cascade — see Component#adoptChildComponents.
     */
    observeDisconnectedComponents() {
        if (typeof MutationObserver === 'undefined') return; // no DOM (eg. a unit env without jsdom)
        if (!this.reaperObserver) {
            const reapHandler = getThrottledFunction(this.reapDisconnectedComponents.bind(this), ReaperThrottleDelay);
            this.reaperObserver = new MutationObserver(reapHandler);
        }
        this.reaperObserver.disconnect(); // re-arm over the current root-node list
        this.rootNodes.forEach((rootNode) => {
            this.reaperObserver.observe(rootNode, {childList: true, subtree: true});
        });
    }

    /**
     * Destroys every registered component whose element has left the DOM.
     * `Component#destroy` deregisters the instance, so the sweep only has to trigger it.
     */
    reapDisconnectedComponents() {
        const registry = ComponentRegistry.getDefault();
        if (!registry) return;

        Object.keys(registry.components).forEach((componentTypeKey) => {
            // Iterate a copy: destroy() splices the instance out of the array we are walking.
            [...registry.getComponentsForType(componentTypeKey)].forEach((instance) => {
                if (!instance || typeof instance.destroy !== 'function') return;
                if (!instance.element) return;

                if (instance.element.isConnected) {
                    instance.hasBeenConnected = true;
                    return;
                }

                // Never in the DOM: a component constructed for an element which is to be inserted
                // later. Not something we have discarded — leave it be.
                if (!instance.hasBeenConnected) return;

                instance.destroy();

                // Dev-only tripwire (esbuild folds process.env.NODE_ENV to a literal, so this whole
                // branch is dead-code-eliminated from production bundles). A correct destroy() deregisters
                // the instance via base Component#destroy(); if it is still registered, its destroy() did
                // not reach base teardown (e.g. an override that skips super.destroy()). This bug is
                // otherwise silent-and-permanent — the instance leaks and is re-poked on every sweep.
                if (process.env.NODE_ENV !== "production") {
                    if (registry.getComponentsForType(componentTypeKey).includes(instance)) {
                        console.warn(
                            `[Reaper] destroy() ran but ${componentTypeKey} is still registered — its ` +
                            `destroy() does not reach base Component#destroy() (missing super.destroy(), or ` +
                            `it overrides destroy() instead of registering teardowns via onDestroy). This ` +
                            `component is leaking. See .claude/skills/js-components/references/js_component_base_class.md.`,
                            instance
                        );
                    }
                }
            });
        });
    }

    observeElementsForComponents() {
        this.componentInitializers.forEach(componentInitializer => {
            componentInitializer.observeElements();
        });
    }

    /**
     * Constructs component instances for each uninitialized element matching the selector.
     * @returns {Array} Array of constructed component instances.
     * @param {ComponentInitializer} componentInitializer - contains the component class, selector, and other configuration.
     */
    construct(componentInitializer) {
        if (!this.componentInitializers.includes(componentInitializer)) this.componentInitializers.push(componentInitializer);
        const klass = componentInitializer.klass;
        return this.uninitializedElements(componentInitializer.selector, componentInitializer.componentTypeKey).map(el => {
            const klassInstance = new klass(el);
            // Seed the reaper's guard: a component is only ever reaped once it has been seen in the
            // DOM, so one constructed for a not-yet-inserted element is not mistaken for a discarded
            // one. Elements are found by querying the root nodes, so in practice this is true.
            klassInstance.hasBeenConnected = !!el.isConnected;
            ComponentConstructor.markElementAsComponentized(el, componentInitializer.componentTypeKey);
            return klassInstance;
        });
    }

    /**
     * Finds all uninitialized elements within the root nodes.
     * @param {string} selector - The CSS selector to match elements.
     * @param {string} componentTypeKey - The component type key.
     * @returns {Array} Array of uninitialized elements.
     */
    uninitializedElements(selector, componentTypeKey) {
        const elements = [];
        this.rootNodes.forEach((rootNode) => {
            if (rootNode.querySelector(selector) === null) return [];  // querySelector() is faster - return quickly if nothing matching
            Array.from(rootNode.querySelectorAll(selector)).forEach((element) => {
                if (!ComponentConstructor.isElementComponentized(element, componentTypeKey)) {
                  elements.push(element);
                }
            })
        });
        return elements;
    }

}

/**
 * Key for the singleton instance of ComponentConstructor on the window object.
 * @type {string}
 */
const RankersComponentConstructorKey = 'ComponentConstructor';

/**
 * We ensure there's a singleton instance of ComponentConstructor because there should only be one instance we ever reference.
 * Because separate bundled JS files need to share the same instance of ComponentConstructor, we will define it on the window object.
 **/
function ensureComponentConstructorInstance() {
    ensureRankersNamespace();
    if (!window[RankersNamespaceKey][RankersComponentConstructorKey]) window[RankersNamespaceKey][RankersComponentConstructorKey] = new ComponentConstructor();
    return window[RankersNamespaceKey][RankersComponentConstructorKey];
}

ensureComponentConstructorInstance();

/**
 * Gets the singleton instance of ComponentConstructor.
 * @returns {ComponentConstructor} The singleton instance of ComponentConstructor.
 */
function getSingletonInstance() {
    return ensureComponentConstructorInstance();
}

/**
 * Constructs components using the singleton instance of ComponentConstructor.
 * It references the singleton instance of ComponentConstructor which holds the root nodes we'll search for elements.
 * @returns {Array} Array of constructed component instances.
 * @param componentInitializer
 */
export const constructComponents = (componentInitializer) => {
    return getSingletonInstance().construct(componentInitializer);
}

/**
 * Adds a root node using the singleton instance of ComponentConstructor.
 * @param {Element} node - The root node to add.
 */
export const addComponentRootNode = (node) => {
    getSingletonInstance().addRootNode(node);
}

/**
 * Removes a root node using the singleton instance of ComponentConstructor.
 * @param {Element} node - The root node to remove.
 */
export const removeComponentRootNode = (node) => {
    getSingletonInstance().removeRootNode(node);
}

/**
 * Gets root nodes using the singleton instance of ComponentConstructor.
 * @returns {Array} Array of current root nodes.
 */
export const getComponentRootNodes = () => {
    return getSingletonInstance().getRootNodes();
}

/**
 * The element "componentizedWith" bookkeeping, exported so that the `Component` base class
 * marks and unmarks elements through the same implementation the constructor reads back with
 * `isElementComponentized`. (These were previously duplicated in component.js, which risked
 * the two drifting apart.)
 */
export const markElementAsComponentized = (element, componentTypeKey) => {
    ComponentConstructor.markElementAsComponentized(element, componentTypeKey);
}

export const unmarkElementAsComponentized = (element, componentTypeKey) => {
    ComponentConstructor.unmarkElementAsComponentized(element, componentTypeKey);
}

export const isElementComponentized = (element, componentTypeKey) => {
    return ComponentConstructor.isElementComponentized(element, componentTypeKey);
}